import { useState, useEffect } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { usePayment } from './usePayment';
import { dollarsToCents } from '../lib/stripe';

export function useDigitalWallet(orderData, onPaymentSuccess, onPaymentError) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  
  const stripe = useStripe();
  const { createPaymentIntent } = usePayment(); // Removed processPayment - we create orders directly after confirmation

  useEffect(() => {
    if (!stripe) return;

    // Create payment request for Apple Pay / Google Pay
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Total',
        amount: dollarsToCents(orderData.total),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: false,
      requestShipping: orderData.fulfillment === 'deliver',
      shippingOptions: orderData.fulfillment === 'deliver' ? [
        {
          id: 'standard',
          label: 'Standard Delivery',
          detail: '2-3 business days',
          amount: dollarsToCents(orderData.serviceFee || 0),
        },
      ] : [],
    });

    // Check if payment request is supported
    pr.canMakePayment().then((result) => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isMac = platform.includes('Mac') || platform.includes('MacIntel') || platform.includes('MacPPC');
      const isMobileSafari = isSafari && isMobile;
      const isSafariOnMac = isSafari && isMac;

      const supportsApplePay = !!(result && result.applePay && (isMobileSafari || isSafariOnMac) && typeof window !== 'undefined' && window.ApplePaySession);
      const supportsGooglePay = !!(result && result.googlePay);

      setApplePayAvailable(supportsApplePay);
      setGooglePayAvailable(supportsGooglePay);
      setCanMakePayment(supportsApplePay || supportsGooglePay);
    }).catch((error) => {
      // Fallback: Enhanced detection for various browsers and devices
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      const isChrome = userAgent.includes('Chrome') && !userAgent.includes('Edg');
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      const isMac = platform.includes('Mac') || platform.includes('MacIntel') || platform.includes('MacPPC');
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      const isChromeOnMac = isChrome && isMac;
      const isSafariOnMac = isSafari && isMac;
      const isMobileChrome = isChrome && isMobile;
      const isMobileSafari = isSafari && isMobile;
      
      if (isChromeOnMac || isMobileChrome) {
        setGooglePayAvailable(true);
        setApplePayAvailable(false);
        setCanMakePayment(true);
      } else if (isSafariOnMac || isMobileSafari) {
        // Only enable Apple Pay if ApplePaySession is available (Safari requirement)
        const applePaySupported = typeof window !== 'undefined' && window.ApplePaySession;
        setApplePayAvailable(!!applePaySupported);
        setGooglePayAvailable(false);
        setCanMakePayment(!!applePaySupported);
      }
    });

    // Handle payment request events
    pr.on('paymentmethod', async (event) => {
      setLoading(true);
      setError('');

      try {
        console.log('[useDigitalWallet] Payment method received:', event);
        
        // Create payment intent
        const paymentIntentResponse = await createPaymentIntent({
          ...orderData,
          amount: dollarsToCents(orderData.total),
          currency: 'usd',
        });

        console.log('[useDigitalWallet] Payment intent created:', paymentIntentResponse);

        // Extract client_secret from response structure
        const clientSecret = paymentIntentResponse?.paymentIntent?.client_secret || paymentIntentResponse?.client_secret;
        
        if (!clientSecret) {
          throw new Error('No client secret returned from payment intent');
        }

        // CRITICAL: Validate inventory BEFORE confirming payment
        // This prevents charging the customer if inventory is insufficient
        console.log('[useDigitalWallet] Validating inventory before payment confirmation...');
        try {
          const { validateInventoryAvailability } = await import('../utils/inventoryApi');
          
          const orderItemsForValidation = (orderData.items || []).map(item => ({
            ...item,
            productId: item.productId || item.id,
            fulfillmentDay: item.fulfillmentDay || null
          }));
          
          const validation = await validateInventoryAvailability(orderItemsForValidation);
          if (!validation.valid) {
            const errorMsg = `Inventory validation failed: ${validation.errors.join(', ')}`;
            console.error('[useDigitalWallet] Inventory validation failed:', validation.errors);
            event.complete('fail');
            setError(errorMsg);
            onPaymentError(errorMsg);
            return;
          }
          console.log('[useDigitalWallet] Inventory validation passed, proceeding with payment confirmation');
        } catch (validationError) {
          console.error('[useDigitalWallet] Inventory validation error:', validationError);
          const errorMsg = validationError.message || 'Failed to validate inventory. Please try again.';
          event.complete('fail');
          setError(errorMsg);
          onPaymentError(errorMsg);
          return;
        }

        // Confirm the payment using the payment method from the event
        const { error: confirmError, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: event.paymentMethod.id,
          },
          {
            handleActions: false,
          }
        );

        if (confirmError) {
          console.error('[useDigitalWallet] Payment confirmation error:', confirmError);
          event.complete('fail');
          setError(confirmError.message);
          onPaymentError(confirmError.message);
          return;
        }

        console.log('[useDigitalWallet] Payment confirmed:', confirmedIntent);
        
        // Validate that confirmedIntent exists and has required fields
        if (!confirmedIntent) {
          console.error('[useDigitalWallet] confirmedIntent is null/undefined after confirmation');
          event.complete('fail');
          setError('Payment confirmation failed - no payment intent returned');
          onPaymentError('Payment confirmation failed - no payment intent returned');
          return;
        }

        // Complete the payment request
        event.complete('success');

        // Payment is already confirmed - directly create the order
        // We skip processPayment since it has issues receiving the third parameter
        // and the payment is already succeeded
        console.log('[useDigitalWallet] Payment already confirmed, creating order directly...');
        
        try {
          // Import paymentService
          const { paymentService } = await import('../lib/paymentService');
          
          // Prepare order data (using orderData prop from parent)
          // Get items from orderData prop passed to the hook
          const orderItems = (orderData?.items || []).map(item => ({
            ...item,
            fulfillmentDay: item.fulfillmentDay || item.pickupDate || item.serviceDate || null
          }));
          
          const orderDataToSubmit = {
            paymentIntentId: confirmedIntent.id,
            items: orderItems,
            total: confirmedIntent.amount / 100, // Convert from cents to dollars
            status: 'confirmed',
            billingDetails: {
              name: event.payerName,
              email: event.payerEmail,
              phone: event.payerPhone || null
            },
            address: event.shippingAddress ? {
              street: event.shippingAddress.addressLine?.[0] || '',
              city: event.shippingAddress.city || '',
              state: event.shippingAddress.region || '',
              zip: event.shippingAddress.postalCode || ''
            } : null,
            serviceFee: orderData?.serviceFee || 2.0,
            deliveryFee: orderData?.deliveryFee || 0
          };

          console.log('[useDigitalWallet] Creating order with data:', {
            paymentIntentId: orderDataToSubmit.paymentIntentId,
            itemCount: orderDataToSubmit.items.length,
            total: orderDataToSubmit.total,
            hasItems: orderDataToSubmit.items.length > 0,
            itemsPreview: orderDataToSubmit.items.map(item => ({ id: item.id, qty: item.qty }))
          });

          if (orderDataToSubmit.items.length === 0) {
            throw new Error('No items in order data');
          }

          const order = await paymentService.createOrder(orderDataToSubmit);
          console.log('[useDigitalWallet] ✅ Order created successfully:', order.id);
          
          // Clear cart after successful order (similar to processPayment)
          try {
            const { useCart } = await import('../contexts/CartContext');
            // Note: This won't work directly - we need the hook context
            // But the order is created, so cart clearing can happen on success page
          } catch (cartError) {
            console.log('[useDigitalWallet] Note: Cart will be cleared on success page');
          }
          
          onPaymentSuccess(order.id);
        } catch (orderError) {
          console.error('[useDigitalWallet] ❌ Error creating order after payment confirmation:', orderError);
          // Payment already succeeded, so we can't undo it
          onPaymentError(orderError.message || 'Payment succeeded but order creation failed. Please contact support.');
        }
      } catch (err) {
        console.error('[useDigitalWallet] Payment error:', err);
        // Complete with failure if event is still available
        if (event && typeof event.complete === 'function') {
          event.complete('fail');
        }
        setError(err.message);
        onPaymentError(err.message);
      } finally {
        setLoading(false);
      }
    });

    pr.on('cancel', () => {
      setError('Payment was cancelled');
    });

    setPaymentRequest(pr);
  }, [stripe, orderData, createPaymentIntent, onPaymentSuccess, onPaymentError]); // Removed processPayment from dependencies

  const handlePaymentRequest = async () => {
    if (!paymentRequest) {
      console.error('[useDigitalWallet] Payment request not available');
      setError('Payment request is not available. Please try again.');
      return;
    }

    try {
      // Ensure payment request is ready before showing
      const canMakePayment = await paymentRequest.canMakePayment();
      if (!canMakePayment) {
        setError('Apple Pay is not available on this device.');
        return;
      }

      // Show the payment request
      paymentRequest.show();
    } catch (err) {
      console.error('[useDigitalWallet] Error showing payment request:', err);
      setError('Failed to initialize payment. Please try again.');
    }
  };

  return {
    canMakePayment,
    applePayAvailable,
    googlePayAvailable,
    loading,
    error,
    handlePaymentRequest,
    paymentRequest
  };
}
